/**
 * バックアップと復元 `/backup`（A143 / A273 / R40 / R88 / A157）。
 *
 * タブは 4 つ:
 *   health       … 各ノードの最終取得時刻と restic の申告
 *   restores     … 復元要求の起票と進行（`restore_runs` の状態機械）
 *   destinations … バックアップ先の登録とパスフレーズの取り出し
 *   drills       … 復旧演習の台帳（実測 RTO / RPO）
 *
 * **なぜ画面が要るか。** CP 側の実装（`cp-backup` / `restore_runs` /
 * `backup_destinations` / `dr_drills`）は R0 で揃ったが、運営が触る口が
 * どこにも無く、復旧手順書は「存在しない画面」を案内していた（A273）。
 * 顧客のデータ復旧要求が静かに消えないよう、滞留が見える形にする。
 *
 * **復元の実行は運営者が手で行う。** 特権ヘルパに restic の restore 動詞が
 * 無いあいだ、CP にできるのは「要求を記録して督促し、結果を書き留める」まで。
 * それを画面でも隠さず、`awaiting_operator` を状態として見せる。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  CheckCircle2,
  DatabaseBackup,
  HardDriveDownload,
  KeyRound,
  Plus,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Input,
  PageHeader,
  StatCard,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import {
  DR_DRILL_KIND_LABEL,
  DR_DRILL_OUTCOME_LABEL,
  DR_DRILL_OUTCOME_TONE,
  RESTORE_OPEN_STATUSES,
  RESTORE_STATUS_LABEL,
  RESTORE_STATUS_TONE,
  labelFrom,
  optionsFromLabels,
  toneFrom,
} from '../lib/ops-labels.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { FormDialog, SecretRevealDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  BackupDestinationEntry,
  BackupDestinationList,
  BackupHealthEntry,
  BackupSecretReveal,
  CountedList,
  DrDrillEntry,
  NodeSummary,
  RestoreRunRow,
} from '../types/api.js';

const routeApi = getRouteApi(RID.backup);

/** これより古い取得は「止まっている」とみなす（CP 側の `backup.cp-verify` と同じ閾値）。 */
const STALE_HOURS = 26;

export function BackupPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader
        title="バックアップ"
        description="各ノードの取得状況・復元要求の進行・保存先の登録・復旧演習の台帳。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'health', label: '健全性', icon: DatabaseBackup },
          { value: 'restores', label: '復元要求', icon: HardDriveDownload },
          { value: 'destinations', label: '保存先', icon: Archive },
          { value: 'drills', label: '復旧演習', icon: CheckCircle2 },
        ]}
      />

      {search.tab === 'health' ? <HealthTab /> : null}
      {search.tab === 'restores' ? <RestoresTab /> : null}
      {search.tab === 'destinations' ? <DestinationsTab /> : null}
      {search.tab === 'drills' ? <DrillsTab /> : null}
    </>
  );
}

/* ------------------------------------------------------------- 健全性 */

function HealthTab(): React.ReactElement {
  const query = useQuery({
    queryKey: qk.backupHealth(),
    queryFn: () => api.get<CountedList<BackupHealthEntry>>('/backup/health'),
    refetchInterval: 60_000,
  });

  const items = query.data?.items ?? [];
  const staleBefore = Date.now() - STALE_HOURS * 3_600_000;
  const isStale = (r: BackupHealthEntry): boolean =>
    !r.configured || r.ranAt === null || new Date(r.ranAt).getTime() < staleBefore;

  const unconfigured = items.filter((r) => !r.configured).length;
  const stale = items.filter((r) => r.configured && isStale(r)).length;
  const failing = items.filter((r) => r.ok === false).length;

  const columns: Array<DataTableColumn<BackupHealthEntry>> = [
    {
      id: 'hostname',
      header: 'ノード',
      cell: (r) => (
        <Link
          to="/nodes/$nodeId"
          params={{ nodeId: r.serverNodeId }}
          search={{ tab: 'overview', range: '24h' }}
          className="font-medium hover:text-primary"
        >
          {r.hostname}
        </Link>
      ),
    },
    {
      id: 'state',
      header: '状態',
      cell: (r) =>
        // 「報告が無い」と「失敗した」を混ぜない。前者は仕掛けが動いていない
        // 可能性があり、打ち手がまったく違う。
        !r.configured ? (
          <Badge variant="destructive" size="sm">
            報告なし
          </Badge>
        ) : r.ok === false ? (
          <Badge variant="destructive" size="sm">
            失敗
          </Badge>
        ) : isStale(r) ? (
          <Badge variant="warning" size="sm">
            {STALE_HOURS} 時間以上前
          </Badge>
        ) : (
          <Badge variant="success" size="sm">
            正常
          </Badge>
        ),
    },
    { id: 'ranAt', header: '最終取得', cell: (r) => formatRelative(r.ranAt) },
    {
      id: 'snapshot',
      header: 'スナップショット',
      hideBelow: 'md',
      cell: (r) =>
        r.snapshotId ? <code className="font-mono text-xs">{r.snapshotId}</code> : '—',
    },
    {
      id: 'restoreCheck',
      header: '復元検査',
      hideBelow: 'lg',
      // 取れているだけでは復旧できる保証にならない。復元まで試したかを別に見せる。
      cell: (r) => formatRelative(r.lastRestoreCheckAt),
    },
    {
      id: 'message',
      header: '備考',
      hideBelow: 'lg',
      cell: (r) => <span className="text-xs text-muted-foreground">{r.message ?? '—'}</span>,
    },
    { id: 'reportedAt', header: '報告', hideBelow: 'lg', cell: (r) => formatRelative(r.reportedAt) },
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="対象ノード" value={formatCount(items.length)} icon={DatabaseBackup} />
        <StatCard
          label="報告なし"
          value={formatCount(unconfigured)}
          tone={unconfigured > 0 ? 'destructive' : 'default'}
        />
        <StatCard label="失敗" value={formatCount(failing)} tone={failing > 0 ? 'destructive' : 'default'} />
        <StatCard label="滞留" value={formatCount(stale)} tone={stale > 0 ? 'warning' : 'default'} />
      </div>

      {unconfigured > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>バックアップの報告が無いノードがあります</AlertTitle>
          <AlertDescription>
            報告が無いのは「取れていない」とは限りませんが、<strong>取れている証拠が無い</strong>状態です。
            保存先タブで <code className="font-mono">backup_destinations</code> を登録し、desired-state を配り直してください
            （保存先が 0 件の間は <code className="font-mono">backup.enabled</code> が false のままです）。
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => r.serverNodeId}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="ノード" />}
      />
    </div>
  );
}

/* ----------------------------------------------------------- 復元要求 */

function RestoresTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canRestore = usePermission(OPS_PERMISSION.backupRestore);
  const [requestOpen, setRequestOpen] = React.useState(false);
  const [completing, setCompleting] = React.useState<RestoreRunRow | null>(null);

  const query = useQuery({
    queryKey: qk.restoreRuns({ openOnly: search.openOnly }),
    // API の `status` は単一値しか受けないので、「未完了だけ」は画面側で絞る。
    queryFn: () => api.get<CountedList<RestoreRunRow>>('/restore-runs', { limit: 200 }),
    refetchInterval: 30_000,
  });

  const all = query.data?.items ?? [];
  const rows = search.openOnly ? all.filter((r) => RESTORE_OPEN_STATUSES.includes(r.status)) : all;
  const awaiting = all.filter((r) => r.status === 'awaiting_operator').length;

  const columns: Array<DataTableColumn<RestoreRunRow>> = [
    {
      id: 'status',
      header: '状態',
      width: 'w-36',
      cell: (r) => (
        <StatusBadge
          status={r.status}
          size="sm"
          dot
          pulse={r.status === 'running'}
          tone={toneFrom(RESTORE_STATUS_TONE, r.status)}
          label={labelFrom(RESTORE_STATUS_LABEL, r.status)}
        />
      ),
    },
    {
      id: 'node',
      header: 'ノード',
      cell: (r) => (
        <Link
          to="/nodes/$nodeId"
          params={{ nodeId: r.server_node_id }}
          search={{ tab: 'overview', range: '24h' }}
          className="hover:text-primary"
        >
          {r.hostname}
        </Link>
      ),
    },
    {
      id: 'snapshot',
      header: 'スナップショット',
      hideBelow: 'md',
      cell: (r) => <code className="font-mono text-xs">{r.snapshot_id}</code>,
    },
    {
      id: 'targets',
      header: '対象',
      hideBelow: 'md',
      cell: (r) => (
        <span className="text-xs text-muted-foreground">{(r.targets ?? []).join(', ') || '—'}</span>
      ),
    },
    {
      id: 'reason',
      header: '理由',
      hideBelow: 'lg',
      cell: (r) => <span className="text-xs text-muted-foreground">{r.reason}</span>,
    },
    {
      id: 'reminder',
      header: '督促',
      align: 'right',
      hideBelow: 'lg',
      // 督促が積み上がっているものは、忘れられている可能性が高い。
      cell: (r) =>
        r.reminder_count > 0 ? (
          <Badge variant="warning" size="sm">
            {r.reminder_count} 回
          </Badge>
        ) : (
          '—'
        ),
    },
    { id: 'createdAt', header: '起票', cell: (r) => formatDateTime(r.created_at) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-32',
      cell: (r) =>
        canRestore && RESTORE_OPEN_STATUSES.includes(r.status) ? (
          <Button variant="outline" size="sm" onClick={() => setCompleting(r)}>
            結果を記録
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      {awaiting > 0 ? (
        <Alert variant="warning">
          <AlertTitle>運営の実施待ちが {awaiting} 件あります</AlertTitle>
          <AlertDescription>
            復元の実行は運営者が手で行います（特権ヘルパに restic の restore 動詞がまだありません）。
            実施したら必ず「結果を記録」を押してください。記録しないと <code className="font-mono">restore.sweep</code> が毎日督促し続けます。
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-panel px-3 py-2.5">
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={search.openOnly}
            onChange={(e) => void navigate({ search: (prev) => ({ ...prev, openOnly: e.target.checked }) })}
            className="size-4 rounded border-border"
          />
          未完了のみ表示
        </label>
        <div className="ml-auto flex items-center gap-2">
          <RefetchingDot active={query.isFetching && !query.isLoading} />
          {canRestore ? (
            <Button size="sm" onClick={() => setRequestOpen(true)}>
              <Plus aria-hidden />
              復元を要求する
            </Button>
          ) : null}
        </div>
      </div>

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={search.openOnly} entityLabel="復元要求" />}
      />

      <RequestRestoreDialog open={requestOpen} onOpenChange={setRequestOpen} />
      <CompleteRestoreDialog run={completing} onClose={() => setCompleting(null)} />
    </div>
  );
}

function RequestRestoreDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [serverNodeId, setServerNodeId] = React.useState('');
  const [snapshotId, setSnapshotId] = React.useState('');
  const [targets, setTargets] = React.useState('');
  const [reason, setReason] = React.useState('');

  const nodes = useQuery({
    queryKey: qk.nodes({ scope: 'restore-picker' }),
    queryFn: () => api.get<{ items: NodeSummary[] }>('/nodes', { limit: 200 }),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) return;
    setServerNodeId('');
    setSnapshotId('');
    setTargets('');
    setReason('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/restore', {
        serverNodeId,
        snapshotId: snapshotId.trim(),
        // 1 行 1 パス。空行は落とす（貼り付けの末尾改行で 400 にしない）。
        targets: targets
          .split('\n')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'backup', 'restore-runs'] });
      onOpenChange(false);
    },
  });

  const targetList = targets.split('\n').filter((s) => s.trim().length > 0);
  // サーバー側の zod（`reason` 10 文字以上・`targets` 1〜100 件）と同じ条件で先に止める。
  const invalid =
    serverNodeId.length === 0 ||
    snapshotId.trim().length === 0 ||
    targetList.length === 0 ||
    targetList.length > 100 ||
    reason.trim().length < 10;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="復元を要求する"
      description="要求は台帳（restore_runs）に残り、実施されるまで毎日督促されます。"
      submitLabel="要求する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="対象ノード" required>
        <NativeSelect
          value={serverNodeId}
          onChange={setServerNodeId}
          options={[
            { value: '', label: '選択してください' },
            ...(nodes.data?.items ?? []).map((n) => ({ value: n.id, label: n.hostname })),
          ]}
        />
      </Field>

      <Field
        label="スナップショット ID"
        required
        hint="restic の snapshot ID。restic snapshots の出力から控えます。"
      >
        <Input
          value={snapshotId}
          onChange={(e) => setSnapshotId(e.target.value)}
          placeholder="a1b2c3d4"
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <Field
        label="復元対象のパス"
        required
        hint="1 行に 1 パス。全体復元は運用手順（手動）に寄せているのでここには書きません。"
      >
        <Textarea
          value={targets}
          onChange={(e) => setTargets(e.target.value)}
          rows={4}
          placeholder={'/srv/vmail/example.com/info\n/etc/postfix/main.cf'}
          spellCheck={false}
        />
      </Field>

      <Field label="理由" required hint="10 文字以上。監査ログに残ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="契約者からの誤削除の申告（チケット #1234）による復元"
        />
      </Field>
    </FormDialog>
  );
}

function CompleteRestoreDialog({
  run,
  onClose,
}: {
  run: RestoreRunRow | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [outcome, setOutcome] = React.useState('succeeded');
  const [note, setNote] = React.useState('');
  const [restoredBytes, setRestoredBytes] = React.useState('');
  const [restoredFiles, setRestoredFiles] = React.useState('');

  React.useEffect(() => {
    if (!run) return;
    setOutcome('succeeded');
    setNote('');
    setRestoredBytes('');
    setRestoredFiles('');
  }, [run]);

  const submit = useMutation({
    mutationFn: () => {
      // 実測値は残す。次の演習と比べられないと「目標に届いているか」を誰も言えない。
      const result: Record<string, number> = {};
      if (restoredBytes.trim().length > 0) result.restoredBytes = Number(restoredBytes);
      if (restoredFiles.trim().length > 0) result.restoredFiles = Number(restoredFiles);
      return api.post(`/restore-runs/${run?.id ?? ''}/complete`, {
        outcome,
        result,
        ...(note.trim().length > 0 ? { note: note.trim() } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'backup', 'restore-runs'] });
      onClose();
    },
  });

  // 失敗・取り消しは理由が無いと台帳として役に立たない。
  const invalid = outcome !== 'succeeded' && note.trim().length === 0;

  return (
    <FormDialog
      open={run !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="復元の結果を記録する"
      description={run ? `${run.hostname} / ${run.snapshot_id}` : undefined}
      submitLabel="記録する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
    >
      <Field label="結果" required>
        <NativeSelect
          value={outcome}
          onChange={setOutcome}
          options={[
            { value: 'succeeded', label: '成功' },
            { value: 'failed', label: '失敗' },
            { value: 'canceled', label: '取り消し' },
          ]}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="復元したバイト数" hint="任意。実測値の記録に使います。">
          <Input
            value={restoredBytes}
            onChange={(e) => setRestoredBytes(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
        <Field label="復元したファイル数" hint="任意。">
          <Input
            value={restoredFiles}
            onChange={(e) => setRestoredFiles(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
      </div>

      <Field
        label="備考"
        required={outcome !== 'succeeded'}
        hint={outcome === 'succeeded' ? '任意。' : '失敗・取り消しのときは必須です。'}
      >
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
      </Field>
    </FormDialog>
  );
}

/* ------------------------------------------------------------- 保存先 */

function DestinationsTab(): React.ReactElement {
  const canRestore = usePermission(OPS_PERMISSION.backupRestore);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [secretFor, setSecretFor] = React.useState<string | null>(null);

  const query = useQuery({
    queryKey: qk.backupDestinations(),
    queryFn: () => api.get<BackupDestinationList>('/backup-destinations'),
  });

  const items = query.data?.items ?? [];
  const offsiteCount = query.data?.offsiteCount ?? 0;

  const columns: Array<DataTableColumn<BackupDestinationEntry>> = [
    { id: 'name', header: '名前', cell: (r) => <span className="font-medium">{r.name}</span> },
    {
      id: 'repository',
      header: 'restic リポジトリ',
      cell: (r) => <code className="block max-w-md break-all font-mono text-xs">{r.repository}</code>,
    },
    {
      id: 'role',
      header: '役割',
      cell: (r) => (
        <Badge variant={r.role === 'primary' ? 'info' : 'muted'} size="sm">
          {r.role === 'primary' ? '一次' : '二次'}
        </Badge>
      ),
    },
    {
      id: 'offsite',
      header: 'オフサイト',
      cell: (r) =>
        r.isOffsite ? (
          <Badge variant="success" size="sm">
            別事業者
          </Badge>
        ) : (
          <Badge variant="muted" size="sm">
            同一事業者
          </Badge>
        ),
    },
    {
      id: 'lock',
      header: 'Object Lock',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => (r.objectLockDays > 0 ? `${r.objectLockDays} 日` : '—'),
    },
    {
      id: 'allowDelete',
      header: '削除権',
      hideBelow: 'lg',
      // 削除を許す鍵は、ランサムウェアに「バックアップごと消す」経路を残す。
      cell: (r) =>
        r.allowDelete ? (
          <Badge variant="destructive" size="sm">
            削除可（非推奨）
          </Badge>
        ) : (
          <Badge variant="success" size="sm">
            追記のみ
          </Badge>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-40',
      cell: (r) =>
        canRestore && r.serverNodeId ? (
          <Button variant="outline" size="sm" onClick={() => setSecretFor(r.serverNodeId)}>
            <KeyRound aria-hidden />
            パスフレーズ
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      {offsiteCount === 0 ? (
        <Alert variant="destructive">
          <AlertTitle>オフサイトの保存先が 1 件もありません</AlertTitle>
          <AlertDescription>
            一次バケットだけの構成は、ランサムウェアで保存先ごと消される手口に無防備です。
            <strong>VPS と別の事業者</strong>のバケットを Object Lock 有効で作り、
            DeleteObject を含まない鍵で登録してください（この状態が続く間は毎日 critical が上がります）。
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="mb-4 flex items-center gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        {canRestore ? (
          <Button size="sm" className="ml-auto" onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden />
            保存先を登録
          </Button>
        ) : null}
      </div>

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="保存先" />}
      />

      <CreateDestinationDialog open={createOpen} onOpenChange={setCreateOpen} />
      <BackupSecretDialog serverNodeId={secretFor} onClose={() => setSecretFor(null)} />
    </div>
  );
}

function CreateDestinationDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [name, setName] = React.useState('');
  const [repository, setRepository] = React.useState('');
  const [role, setRole] = React.useState('secondary');
  const [isOffsite, setIsOffsite] = React.useState(true);
  const [objectLockDays, setObjectLockDays] = React.useState('2555');
  const [serverNodeId, setServerNodeId] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [accessKeyId, setAccessKeyId] = React.useState('');
  const [secretAccessKey, setSecretAccessKey] = React.useState('');
  const [region, setRegion] = React.useState('');

  const nodes = useQuery({
    queryKey: qk.nodes({ scope: 'destination-picker' }),
    queryFn: () => api.get<{ items: NodeSummary[] }>('/nodes', { limit: 200 }),
    enabled: open,
  });

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setRepository('');
    setRole('secondary');
    setIsOffsite(true);
    setObjectLockDays('2555');
    setServerNodeId('');
    setNotes('');
    setAccessKeyId('');
    setSecretAccessKey('');
    setRegion('');
  }, [open]);

  const hasCredentials = accessKeyId.length > 0 || secretAccessKey.length > 0;

  const submit = useMutation({
    mutationFn: () =>
      api.post('/backup-destinations', {
        name: name.trim(),
        repository: repository.trim(),
        role,
        isOffsite,
        // 削除権は UI から与えない。**削除できる鍵を登録する導線を作らない**
        // （必要になったら API を直接叩く、という摩擦を残しておく）。
        allowDelete: false,
        objectLockDays: Number(objectLockDays || '0'),
        serverNodeId: serverNodeId.length > 0 ? serverNodeId : null,
        ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}),
        ...(hasCredentials
          ? {
              credentials: {
                accessKeyId: accessKeyId.trim(),
                secretAccessKey: secretAccessKey.trim(),
                ...(region.trim().length > 0 ? { region: region.trim() } : {}),
              },
            }
          : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.backupDestinations() });
      onOpenChange(false);
    },
  });

  // サーバー側の zod（`repository` 8 文字以上、資格情報を保存するならノード必須）。
  const invalid =
    name.trim().length === 0 ||
    repository.trim().length < 8 ||
    (hasCredentials && (serverNodeId.length === 0 || accessKeyId.length === 0 || secretAccessKey.length === 0));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="バックアップ先を登録する"
      description="バケットの作成・Object Lock の有効化・追記のみの鍵の発行は、クラウド事業者のコンソールで先に済ませてください。"
      submitLabel="登録する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="名前" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="オフサイト（B 社 / 東京）" />
      </Field>

      <Field label="restic リポジトリ" required hint="s3:https://…/バケット名/パス の表記。">
        <Input
          value={repository}
          onChange={(e) => setRepository(e.target.value)}
          placeholder="s3:https://s3.example.net/mailsv-backup/node-01"
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="役割" required>
          <NativeSelect
            value={role}
            onChange={setRole}
            options={[
              { value: 'primary', label: '一次' },
              { value: 'secondary', label: '二次' },
            ]}
          />
        </Field>
        <Field label="Object Lock の保持日数" hint="0 なら未設定。既定の 2555 日は 7 年。">
          <Input
            value={objectLockDays}
            onChange={(e) => setObjectLockDays(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={isOffsite}
          onChange={(e) => setIsOffsite(e.target.checked)}
          className="size-4 rounded border-border"
        />
        VPS とは別の事業者（オフサイト）
      </label>

      <Field label="対象ノード" hint="資格情報を保存する場合は必須です。空なら CP 共通の保存先として登録します。">
        <NativeSelect
          value={serverNodeId}
          onChange={setServerNodeId}
          options={[
            { value: '', label: '（指定しない）' },
            ...(nodes.data?.items ?? []).map((n) => ({ value: n.id, label: n.hostname })),
          ]}
        />
      </Field>

      <Alert variant="warning">
        <AlertDescription>
          S3 の資格情報を入力すると、その場で暗号化して <code className="font-mono">node_secrets</code> に保存されます。
          <strong>DeleteObject を含まない鍵</strong>を使ってください。
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="アクセスキー ID">
          <Input
            value={accessKeyId}
            onChange={(e) => setAccessKeyId(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </Field>
        <Field label="シークレットキー">
          <Input
            type="password"
            value={secretAccessKey}
            onChange={(e) => setSecretAccessKey(e.target.value)}
            autoComplete="new-password"
          />
        </Field>
        <Field label="リージョン">
          <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="ap-northeast-1" />
        </Field>
      </div>

      <Field label="備考">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/**
 * restic パスフレーズの取り出し。
 *
 * ノードが全損したあと S3 のバックアップを復号するために要る。理由を書かせて
 * 同期監査に残す（通信の秘密に触れる操作の統制）。値は 1 回しか見せない。
 */
function BackupSecretDialog({
  serverNodeId,
  onClose,
}: {
  serverNodeId: string | null;
  onClose: () => void;
}): React.ReactElement {
  const [kind, setKind] = React.useState('restic');
  const [reason, setReason] = React.useState('');
  const [revealed, setRevealed] = React.useState<BackupSecretReveal | null>(null);

  React.useEffect(() => {
    if (!serverNodeId) return;
    setKind('restic');
    setReason('');
    setRevealed(null);
  }, [serverNodeId]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<BackupSecretReveal>(`/nodes/${serverNodeId ?? ''}/backup-secret`, {
        kind,
        reason: reason.trim(),
      }),
    onSuccess: (data) => setRevealed(data),
  });

  return (
    <>
      <FormDialog
        open={serverNodeId !== null && revealed === null}
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        title="バックアップの秘密を取り出す"
        description="復旧作業専用です。誰がいつ何のために取り出したかが監査ログに残ります。"
        submitLabel="取り出す"
        pending={submit.isPending}
        error={submit.error}
        disabled={reason.trim().length < 10}
        onSubmit={() => submit.mutate()}
      >
        <Field label="種別" required>
          <NativeSelect
            value={kind}
            onChange={setKind}
            options={[
              { value: 'restic', label: 'restic のパスフレーズ' },
              { value: 'backup_s3', label: 'S3 の資格情報' },
            ]}
          />
        </Field>
        <Field label="理由" required hint="10 文字以上。">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="ノード node-01 の全損に伴う復旧作業（インシデント INC-2026-0007）"
          />
        </Field>
      </FormDialog>

      <SecretRevealDialog
        open={revealed !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRevealed(null);
            onClose();
          }
        }}
        title="バックアップの秘密"
        description={revealed?.notice}
        items={
          revealed
            ? [
                { label: '鍵 ID', value: revealed.keyId },
                { label: `値（version ${revealed.version}）`, value: revealed.material },
              ]
            : []
        }
      />
    </>
  );
}

/* ----------------------------------------------------------- 復旧演習 */

function DrillsTab(): React.ReactElement {
  const canRestore = usePermission(OPS_PERMISSION.backupRestore);
  const [recordOpen, setRecordOpen] = React.useState(false);

  const list = useCursorList<DrDrillEntry>({
    path: '/jobs/dr-drills',
    keyFor: (params) => qk.drDrills(params),
    filters: {},
  });

  const columns: Array<DataTableColumn<DrDrillEntry>> = [
    { id: 'performedAt', header: '実施日時', cell: (r) => formatDateTime(r.performedAt) },
    { id: 'kind', header: '種別', cell: (r) => labelFrom(DR_DRILL_KIND_LABEL, r.kind) },
    {
      id: 'outcome',
      header: '結果',
      cell: (r) => (
        <StatusBadge
          status={r.outcome}
          size="sm"
          dot
          tone={toneFrom(DR_DRILL_OUTCOME_TONE, r.outcome)}
          label={labelFrom(DR_DRILL_OUTCOME_LABEL, r.outcome)}
        />
      ),
    },
    {
      id: 'source',
      header: '実施者',
      hideBelow: 'md',
      cell: (r) => (
        <Badge variant={r.source === 'operator' ? 'muted' : 'info'} size="sm">
          {r.source === 'operator' ? '人手' : '自動'}
        </Badge>
      ),
    },
    {
      id: 'rto',
      header: '実測 RTO',
      align: 'right',
      // 目標と実測を並べる。並べないと「目標に届いているか」を誰も言えない。
      cell: (r) =>
        r.elapsedMinutes === null ? (
          '—'
        ) : (
          <span className={r.targetRtoMinutes !== null && r.elapsedMinutes > r.targetRtoMinutes ? 'text-destructive' : undefined}>
            {r.elapsedMinutes} 分
            {r.targetRtoMinutes !== null ? (
              <span className="text-muted-foreground"> / 目標 {r.targetRtoMinutes} 分</span>
            ) : null}
          </span>
        ),
    },
    {
      id: 'rpo',
      header: '実測 RPO',
      align: 'right',
      hideBelow: 'lg',
      cell: (r) => (r.rpoMinutes === null ? '—' : `${r.rpoMinutes} 分`),
    },
    {
      id: 'target',
      header: '対象',
      hideBelow: 'lg',
      cell: (r) => <span className="text-xs text-muted-foreground">{r.target || '—'}</span>,
    },
  ];

  return (
    <div className="space-y-5">
      <Alert>
        <AlertTitle>失敗した演習も記録してください</AlertTitle>
        <AlertDescription>
          記録しない運用にすると台帳は必ず全部「合格」になり、台帳としての意味が消えます。
          RPO / RTO の掲示値は演習で実測するまで机上の数字です。
        </AlertDescription>
      </Alert>

      <div className="mb-4 flex items-center gap-2">
        <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
        {canRestore ? (
          <Button size="sm" className="ml-auto" onClick={() => setRecordOpen(true)}>
            <Plus aria-hidden />
            演習を記録
          </Button>
        ) : null}
      </div>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(r) => r.id}
        loading={list.query.isLoading}
        empty={<ListEmpty filtered={false} entityLabel="演習の記録" />}
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <RecordDrillDialog open={recordOpen} onOpenChange={setRecordOpen} />
    </div>
  );
}

function RecordDrillDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [kind, setKind] = React.useState('cp_restore');
  const [outcome, setOutcome] = React.useState('passed');
  const [target, setTarget] = React.useState('');
  const [snapshotId, setSnapshotId] = React.useState('');
  const [targetRto, setTargetRto] = React.useState('');
  const [elapsed, setElapsed] = React.useState('');
  const [rpo, setRpo] = React.useState('');
  const [notes, setNotes] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setKind('cp_restore');
    setOutcome('passed');
    setTarget('');
    setSnapshotId('');
    setTargetRto('');
    setElapsed('');
    setRpo('');
    setNotes('');
  }, [open]);

  const numeric = (v: string): number | undefined => (v.trim().length > 0 ? Number(v) : undefined);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/jobs/dr-drills', {
        kind,
        outcome,
        ...(target.trim().length > 0 ? { target: target.trim() } : {}),
        ...(snapshotId.trim().length > 0 ? { snapshotId: snapshotId.trim() } : {}),
        ...(numeric(targetRto) !== undefined ? { targetRtoMinutes: numeric(targetRto) } : {}),
        ...(numeric(elapsed) !== undefined ? { elapsedMinutes: numeric(elapsed) } : {}),
        ...(numeric(rpo) !== undefined ? { rpoMinutes: numeric(rpo) } : {}),
        ...(notes.trim().length > 0 ? { notes: notes.trim() } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'backup', 'dr-drills'] });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="復旧演習を記録する"
      submitLabel="記録する"
      pending={submit.isPending}
      error={submit.error}
      onSubmit={() => submit.mutate()}
      wide
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="種別" required>
          <NativeSelect value={kind} onChange={setKind} options={optionsFromLabels(DR_DRILL_KIND_LABEL)} />
        </Field>
        <Field label="結果" required>
          <NativeSelect
            value={outcome}
            onChange={setOutcome}
            options={optionsFromLabels(DR_DRILL_OUTCOME_LABEL)}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="対象" hint="ノードのホスト名やデータベース名など。">
          <Input value={target} onChange={(e) => setTarget(e.target.value)} />
        </Field>
        <Field label="スナップショット ID">
          <Input value={snapshotId} onChange={(e) => setSnapshotId(e.target.value)} spellCheck={false} />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="目標 RTO（分）">
          <Input
            value={targetRto}
            onChange={(e) => setTargetRto(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
        <Field label="実測 RTO（分）" hint="覚知から利用可能に戻るまで。">
          <Input
            value={elapsed}
            onChange={(e) => setElapsed(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
          />
        </Field>
        <Field label="実測 RPO（分）" hint="失われたデータの時間幅。">
          <Input value={rpo} onChange={(e) => setRpo(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" />
        </Field>
      </div>

      <Field label="所見">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
      </Field>
    </FormDialog>
  );
}
